import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { type SxProps, type Theme } from "@mui/material/styles";
import { useCallback, useMemo, useState } from "react";

import { isConfirmationMatch } from "./piece-backfill-confirmation";

const styles = {
  warning: { mb: 2 },
  field: { mt: 2 },
} satisfies Record<string, SxProps<Theme>>;

/** Props for {@link PieceBackfillExecuteDialog}. */
export type PieceBackfillExecuteDialogProps = {
  /** Whether the dialog is open. */
  readonly open: boolean;
  /** The company being backfilled; its name is the confirmation phrase. */
  readonly companyName: string;
  /** How many pieces the matching dry run planned to create. */
  readonly plannedPieceCount: number;
  /** True while the execute mutation is in flight. */
  readonly submitting: boolean;
  /** Closes the dialog without executing. */
  readonly onCancel: () => void;
  /** Starts the EXECUTE run. */
  readonly onConfirm: () => void;
};

/**
 * Typed confirmation for an EXECUTE run. The operator must type the target
 * company's name exactly, which makes it hard to execute against the wrong
 * tenant by muscle memory after a dry run.
 *
 * Clearing the typed text on close means reopening the dialog always starts from
 * an unconfirmed state.
 */
const PieceBackfillExecuteDialog = ({
  open,
  companyName,
  plannedPieceCount,
  submitting,
  onCancel,
  onConfirm,
}: PieceBackfillExecuteDialogProps) => {
  const [typed, setTyped] = useState("");

  const confirmed = useMemo(
    () => isConfirmationMatch(typed, companyName),
    [typed, companyName],
  );

  const handleCancel = useCallback(() => {
    setTyped("");
    onCancel();
  }, [onCancel]);

  const handleConfirm = useCallback(() => {
    setTyped("");
    onConfirm();
  }, [onConfirm]);

  return (
    <Dialog fullWidth open={open} maxWidth="sm" onClose={handleCancel}>
      <DialogTitle>Execute piece backfill</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={styles.warning}>
          This writes {plannedPieceCount} piece rows into the live database for{" "}
          {companyName}. The backfill is idempotent and safe to re-run, but
          nothing undoes it automatically: reversing it means deleting the
          created pieces by hand.
        </Alert>
        <DialogContentText>
          Type the company name to confirm: <strong>{companyName}</strong>
        </DialogContentText>
        <TextField
          fullWidth
          autoFocus
          label="Company name"
          value={typed}
          disabled={submitting}
          sx={styles.field}
          onChange={(event) => {
            setTyped(event.target.value);
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button disabled={submitting} onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          color="warning"
          variant="contained"
          disabled={!confirmed || submitting}
          onClick={handleConfirm}
        >
          {submitting ? "Starting…" : "Execute backfill"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PieceBackfillExecuteDialog;
